/*
 * Proprietary and Confidential
 *
 * Copyright (c) [2018] -  [] Marcelo H. Krebber - Munich, London 2018
 * All Rights Reserved.
 *
 * Dissemination or reproduction of this file [buttons.js] or parts within
 * via any medium is strictly forbidden unless prior written permission is obtained
 * from <marcelo.krebber@gmail.com>
 *
 * Last modified: 22.01.20, 16:19
 */
import React, {useEffect, useRef} from 'react';
import {Link} from 'react-router-dom';
import Waves from 'node-waves';
import {showHtmlTooltip, showTooltip} from "../../../actions/tippy-config";
import {cn} from "../../util/text-utils";
import {Check} from "@material-ui/icons";
import {CheckSolidIcon} from "../icons/nav-icons";

const createWaves = (button, circle, float) => {
    const effects = ['waves-effect'];
    button && effects.push('waves-button');
    circle && effects.push('waves-circle');
    float && effects.push('waves-float');
    return effects;
}

// basic boxed link
export const BoxedLink = ({className, children, to = null, ...otherProps}) => {
    return <Link to={to} className={cn('box-aligned', [className])} {...otherProps}>
        {children}
    </Link>
}

// basic Link element
export const ButtonLink = ({children, className, large, block, to = '', ...otherProps}) => {
    return <div className={cn([block, 'd-grid gap-2'])}>
        <Link to={to}
              className={cn('btn btn-primary', [className], [large, 'btn-lg'])}
              {...otherProps}>
            {children}
        </Link>
    </div>
}

// basic button element
export const Button = ({children, className, right = false, large = false, block = false, ...otherProps}) => {
    return <div className={cn([right, 'clearfix'], [block, 'd-grid gap-2'])}>
        <button className={cn('btn btn-primary', [right, 'float-end'], [className], [large, 'btn-lg'])}
                {...otherProps}>
            {children}
        </button>
    </div>
}

// raw icon with tooltip and optional waves
// TODO @Deprecated - delete
export const TooltipIcon = ({children, className, ...otherProps}) => {
    const buttonRef = useRef(null);
    const tooltip = useRef(null);

    useEffect(() => {
        try {
            if (buttonRef.current) {
                tooltip.current = showTooltip(buttonRef.current);
            }
        } catch (error) {
            console.log('Error. TooltipIcon failed on', className)
        }

        return () => tooltip.current && tooltip.current.destroy();
    }, [className]);

    return <i className={className} ref={buttonRef} {...otherProps}>
        {children}
    </i>
}

// simple wrapper for a tooltip
export const Tooltip = ({children, title, ...otherProps}) => {
    const tooltipRef = useRef(null);

    useEffect(() => {
        try {
            if (title && tooltipRef.current) {
                tooltipRef.current = showTooltip(tooltipRef.current);
            }
        } catch (error) {
           console.log('Error. Tooltip failed on', title)
        }

        return () => tooltipRef.current && tooltipRef.current.destroy();
    }, [title]);

    return <div title={title} ref={tooltipRef} {...otherProps}>
        {children}
    </div>
}


// display simple tooltip thru title
export const TooltipLink = ({children, title, to, waves = null, ...otherProps}) => {
    const buttonRef = useRef(null);
    const tooltipRef = useRef(null);

    useEffect(() => {
        if (buttonRef.current) {
            try {
                if (title) {
                    tooltipRef.current = showTooltip(buttonRef.current);
                }
                if (waves) {
                    Waves.attach(buttonRef.current, waves);
                }
            } catch(error) {
                console.log('Error on TooltipLink', title, to)
            }

        }
        return () => tooltipRef.current && tooltipRef.current.destroy();
    }, [to, title]);

    return <Link ref={buttonRef} title={title} to={to} {...otherProps}>
        {children}
    </Link>
}

// displays html content as tooltip with a Link
export const HtmlTooltipLink = ({
                                    children, html = null, htmlParams = null, to,
                                    waves = null, ...otherProps
                                }) => {
    const buttonRef = useRef(null);
    const tooltipRef = useRef(null);

    useEffect(() => {
        try {
            if (buttonRef.current) {
                if (html) {
                    tooltipRef.current = showHtmlTooltip(buttonRef.current, html, htmlParams);
                }
                if (waves) {
                    Waves.attach(buttonRef.current, waves);
                }
            }
        } catch(error) {
           console.log('Error on HtmlTooltipLink', to, html)
        }

        return () => tooltipRef.current && tooltipRef.current.destroy();
    }, [to, html]);

    return <Link ref={buttonRef} to={to} {...otherProps}>
        {children}
    </Link>
}

// displays html content in a tooltip
export const HtmlTooltipContent = ({children, html = null, htmlParams = null, ...otherProps}) => {
    const buttonRef = useRef(null);
    const tooltipRef = useRef(null);

    useEffect(() => {
        try {
            if (buttonRef.current) {
                if (html) {
                    tooltipRef.current = showHtmlTooltip(buttonRef.current, html, htmlParams);
                }
            }
        } catch(error) {
           console.log('Error on HtmlTooltipContent', html)
        }

        return () => tooltipRef.current && tooltipRef.current.destroy();
    }, [html]);

    return <div ref={buttonRef} {...otherProps}>
        {children}
    </div>
}


// raw button with tooltip and optional waves
export const TooltipButton = ({children, title, block = false, waves = null, ...otherProps}) => {
    const buttonRef = useRef(null);
    const tooltipRef = useRef(null);

    useEffect(() => {
        try {
            if (buttonRef.current) {
                if (title) {
                    tooltipRef.current = showTooltip(buttonRef.current);
                }
                if (waves) {
                    Waves.attach(buttonRef.current, waves);
                }
            }
        } catch(error) {
            console.log('Error on TooltipButton', title)
        }

        return () => tooltipRef.current && tooltipRef.current.destroy();
    }, [title]);

    return <div className={cn([block, 'd-grid gap-2'])}>
        <button type='button' ref={buttonRef} title={title}
                {...otherProps}>
            {children}
        </button>
    </div>
}

// preferred button component with tooltip and waves enabled
// options thru className for ex btn-primary for a solid background or rounded-button-small for small icons
// export const RoundedButton = ({children, className, onBound, flat, div = false, ...otherProps}) => {
//     const effects = useRef(createWaves(true, true, false));
//     const tooltipRef = useRef(null);
//     const buttonRef = useRef(null);
//
//     useEffect(() => {
//         try {
//             if (buttonRef.current) {
//                 Waves.attach(buttonRef.current, effects.current);
//                 onBound ? onBound(buttonRef.current) : (tooltipRef.current = showTooltip(buttonRef.current));
//             }
//         } catch(error) {
//            console.log('Error on RoundedButton', className)
//         }
//
//         return () => tooltipRef.current && tooltipRef.current.destroy();
//     }, []);
//
//     const buildClass = cn('btn rounded-button-regular',
//         [className], 'rounded-button', [flat, 'rounded-button-flat']);
//     return div ?
//         <div className={buildClass} ref={buttonRef} {...otherProps}>
//             {children}
//         </div>
//         : <button className={buildClass} ref={buttonRef} {...otherProps}>
//             {children}
//         </button>
// }

export const RoundedButton = ({children, className, onBound, flat, div = false, ...otherProps}) => {
    const effects = useRef(createWaves(true, true, false));
    const tooltipRef = useRef(null);
    const buttonRef = useRef(null);

    useEffect(() => {
        try {
            if (buttonRef.current) {
                Waves.attach(buttonRef.current, effects.current);
                onBound ? onBound(buttonRef.current) : (tooltipRef.current = showTooltip(buttonRef.current));
            }
        } catch(error) {
            console.log('Error on RoundedButton', className)
        }

        return () => tooltipRef.current && tooltipRef.current.destroy();
    }, []);

    const buildClass = cn('btn rounded-button-regular',
        [className], 'rounded-button', [flat, 'rounded-button-flat']);
    return div ?
        <div className={buildClass} ref={buttonRef} {...otherProps}>
            {children}
        </div>
        : <button className={buildClass} ref={buttonRef} {...otherProps}>
            {children}
        </button>
}

export const EmojiButton = ({children, className, selected, disabled, ...otherProps}) => {
    return <div className={cn('navigation-emoji',
        [disabled, 'navigation-emoji-disabled'],
        [className],
        [selected, 'navigation-emoji-selected'])} {...otherProps}>
        {selected && <span className="navigation-emoji-static"><CheckSolidIcon/></span>}
        {children}
    </div>
};


export const TooltipBiggerIcon = ({children, className, ...otherProps}) => {
    const tooltipRef = useRef(null);
    const tooltip = useRef(null);

    useEffect(() => {
        tooltip.current = showTooltip(tooltipRef.current);
        return () => tooltip.current && tooltip.current.destroy();
    }, []);

    return <i className={cn('navigation-icon-bigger', [className])}
              {...otherProps} ref={tooltipRef}>
        {children}
    </i>
};

export const WavesDefaultButton = ({children, className, block = false, ...otherProps}) => {
    return <TooltipButton className={cn('btn btn-primary', [block, 'w-100'], [className])}
                          waves={['waves-effect']}
                          {...otherProps} block>
        {children}
    </TooltipButton>
};

// bounded (custom tooltip) and unbounded (default tooltip) support
export const WavesTooltipBounded = ({
                                        children, className, button, circle, float, flex, btn, primary,
                                        smaller, bigger, onBound, ...otherProps
                                    }) => {
    const effects = useRef(createWaves(button, circle, float));
    const tooltipRef = useRef(null);
    const buttonRef = useRef(null);

    useEffect(() => {
        if (buttonRef.current) {
            Waves.attach(buttonRef.current, effects.current);
            onBound ? onBound(buttonRef.current) : (tooltipRef.current = showTooltip(buttonRef.current));
        }
        return () => tooltipRef.current && tooltipRef.current.destroy();
    }, []);

    return <div className={cn('navigation-flat-icon',
        [btn, 'btn btn-overrides'],
        [primary, 'btn-primary'],
        [smaller, 'navigation-icon-small'],
        [bigger, 'navigation-icon-bigger'],
        [flex, 'btn-flex-centered'],
        [circle, 'border-radius-circle'],
        [className])} ref={buttonRef} {...otherProps}>
        {children}
    </div>
}

export const WavesTooltipButton = ({
                                       children, className, button, circle, float, btn, primary, small,
                                       block = false, title, ...otherProps
                                   }) => {
    const effects = useRef(createWaves(button, circle, float));

    return <TooltipButton className={cn('navigation-flat-button',
        [btn, 'btn btn-overrides'],
        [primary, 'btn-primary'],
        [small, 'btn-sm'],
        [block, 'w-100'],
        [className])} title={title} waves={effects.current}  {...otherProps} block>
        {children}
    </TooltipButton>
};

export const WavesTooltipLink = ({
                                     children, className, to, button, circle, float, btn, primary, small,
                                     title = null, flex, ...otherProps
                                 }) => {
    const effects = useRef(createWaves(button, circle, float));

    return <TooltipLink className={cn('navigation-flat-button',
        [btn, 'btn btn-overrides'],
        [primary, 'btn-primary'],
        [small, 'btn-sm'],
        [flex, 'd-flex align-items-center justify-content-center'],
        [className])} to={to} title={title} waves={effects.current} {...otherProps}>
        {children}
    </TooltipLink>
};


